import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTransfer } from "@/features/transfers/api";
import { TransferStatusBadge } from "@/features/transfers/badges";
import { ApprovalPanel } from "@/features/transfers/ApprovalPanel";
import { GatePassButton } from "@/features/transfers/GatePass";
import { fmtDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/transfers/$transferId")({
  component: TransferDetail,
});

function TransferDetail() {
  const { transferId } = Route.useParams();
  const navigate = useNavigate();
  const { data: t, isLoading } = useTransfer(transferId);

  if (isLoading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!t) return <div className="py-20 text-center text-muted-foreground">You do not have access to this transfer record.</div>;

  const events = t.events || [];

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/transfers" })}><ArrowLeft className="mr-1 h-4 w-4" />Back to Transfers</Button>

      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-3 p-5">
          <div className="space-y-1">
            <Badge variant="outline" className="font-mono">{t.transfer_no}</Badge>
            <div className="text-sm">
              <span className="font-mono text-xs">{t.asset_code}</span> · {t.asset_name}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TransferStatusBadge status={t.status} />
              <Badge variant={t.is_external ? "destructive" : "secondary"}>{t.is_external ? "External" : "Internal"}</Badge>
              {t.special_approval_required && <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">Regulatory</Badge>}
            </div>
          </div>
          <GatePassButton transfer={t} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Movement</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2 text-sm">
          <Info label="From">{[t.from_branch_name, t.from_dept_name].filter(Boolean).join(" / ") || "—"}</Info>
          <Info label="To">{[t.to_branch_name, t.to_dept_name].filter(Boolean).join(" / ") || "—"}</Info>
          <Info label="Reason">{t.reason ?? "—"}</Info>
          <Info label="Remarks">{t.notes ?? "—"}</Info>
          {t.is_external && <Info label="Gate Pass No">{t.gate_pass_no ?? "—"}</Info>}
          {t.special_approval_required && <Info label="Special Approval Remarks">{t.special_approval_remarks ?? "—"}</Info>}
          {t.acknowledged_at && <Info label="Acknowledged">{fmtDateTime(t.acknowledged_at)}</Info>}
          {t.rejection_remarks && <Info label="Rejection Remarks">{t.rejection_remarks}</Info>}
        </CardContent>
      </Card>

      <ApprovalPanel transfer={t} />

      <Card>
        <CardHeader><CardTitle className="text-base">History</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {events.length === 0 && <div className="text-sm text-muted-foreground">No events yet.</div>}
          {events.map((e) => (
            <div key={e.id} className="flex justify-between gap-3 border-b py-2 text-sm last:border-0">
              <div>
                <div className="font-medium">
                  {e.from_status ? `${e.from_status} → ` : ""}{e.to_status}
                  <span className="text-muted-foreground font-normal ml-2">by {e.actor_name || e.actor || "System"}</span>
                </div>
                {e.remarks && <div className="text-xs text-muted-foreground">{e.remarks}</div>}
              </div>
              <div className="text-xs text-muted-foreground">{fmtDateTime(e.created_at)}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
}
